import { lazy, Suspense, useState, useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClient } from '@/lib/queryClient'
import { useAuthStore } from '@/stores/authStore'
import { AdminLayout } from '@/components/layout'
import { AuthLayout } from '@/components/layout/AuthLayout'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { PermissionRoute } from '@/components/auth/PermissionRoute'

import { Toaster } from '@/components/ui/Toast'

// Lazy-loaded pages
const Login = lazy(() => import('@/pages/auth/Login'))
const ForgotPassword = lazy(() => import('@/pages/auth/ForgotPassword'))
const ResetPassword = lazy(() => import('@/pages/auth/ResetPassword'))
const Dashboard = lazy(() => import('@/pages/dashboard/Dashboard'))
const TenantsPage = lazy(() => import('@/pages/tenants/TenantsPage'))
const TenantDetail = lazy(() => import('@/pages/tenants/TenantDetail'))
const UsersPage = lazy(() => import('@/pages/users/UsersPage'))
const UserDetail = lazy(() => import('@/pages/users/UserDetail'))
const AnalyticsPage = lazy(() => import('@/pages/analytics/AnalyticsPage'))
const AuditPage = lazy(() => import('@/pages/audit/AuditPage'))
const AuditDetail = lazy(() => import('@/pages/audit/AuditDetail'))
const TicketsPage = lazy(() => import('@/pages/support/TicketsPage'))
const TicketDetail = lazy(() => import('@/pages/support/TicketDetail'))
const JobsPage = lazy(() => import('@/pages/jobs/JobsPage'))
const JobDetail = lazy(() => import('@/pages/jobs/JobDetail'))
const NotificationsPage = lazy(() => import('@/pages/notifications/NotificationsPage'))
const NotificationNew = lazy(() => import('@/pages/notifications/NotificationNew'))
const NotificationDetail = lazy(() => import('@/pages/notifications/NotificationDetail'))
const StatusPage = lazy(() => import('@/pages/status/StatusPage'))
const WebhooksPage = lazy(() => import('@/pages/webhooks/WebhooksPage'))
const WebhookDetail = lazy(() => import('@/pages/webhooks/WebhookDetail'))
const BillingAuditPage = lazy(() => import('@/pages/billing/BillingAuditPage'))
const PlansPage = lazy(() => import('@/pages/billing/PlansPage'))
const CouponsPage = lazy(() => import('@/pages/billing/CouponsPage'))
const OAuthOverviewPage = lazy(() => import('@/pages/oauth/OAuthOverviewPage'))
const OAuthClientDetailPage = lazy(() => import('@/pages/oauth/OAuthClientDetailPage'))
const WaitlistPage = lazy(() => import('@/pages/waitlist/WaitlistPage'))
const SurveysPage = lazy(() => import('@/pages/surveys/SurveysPage'))
const SurveyDetailPage = lazy(() => import('@/pages/surveys/SurveyDetailPage'))
const BlacklistPage = lazy(() => import('@/pages/blacklist/BlacklistPage'))
const DPAPage = lazy(() => import('@/pages/dpa/DPAPage'))
const DPADetail = lazy(() => import('@/pages/dpa/DPADetail'))
const AccountsPage = lazy(() => import('@/pages/accounts/AccountsPage'))
const AccountDetailPage = lazy(() => import('@/pages/accounts/AccountDetailPage'))
const ReviewQueuePage = lazy(() => import('@/pages/review/ReviewQueuePage'))
const ReviewDetail = lazy(() => import('@/pages/review/ReviewDetail'))
const SettingsPage = lazy(() => import('@/pages/settings/SettingsPage'))
const LiveChatPage = lazy(() => import('@/pages/support/LiveChatPage'))
const RolesPage = lazy(() => import('@/pages/roles/RolesPage'))
const RoleEditPage = lazy(() => import('@/pages/roles/RoleEditPage'))
const FeedbackPage = lazy(() => import('@/pages/feedback/FeedbackPage'))
const ContentCategoriesPage = lazy(() => import('@/pages/content-categories/ContentCategoriesPage'))
const ApiVersionsPage = lazy(() => import('@/pages/api-versions/ApiVersionsPage'))
const RuntimeSettingsPage = lazy(() => import('@/pages/runtime-settings/RuntimeSettingsPage'))
const CountryDocumentTypesPage = lazy(() => import('@/pages/country-document-types/CountryDocumentTypesPage'))
const VerificationsPage = lazy(() => import('@/pages/verifications/VerificationsPage'))
const VerificationDetail = lazy(() => import('@/pages/verifications/VerificationDetail'))
const WebhookDeliveriesPage = lazy(() => import('@/pages/webhook-deliveries/WebhookDeliveriesPage'))
const ContentCategoryNew = lazy(() => import('@/pages/content-categories/ContentCategoryNew'))
const ContentCategoryDetail = lazy(() => import('@/pages/content-categories/ContentCategoryDetail'))
const VLMProvidersPage = lazy(() => import('@/pages/vlm-providers/VLMProvidersPage'))
const VLMProviderNew = lazy(() => import('@/pages/vlm-providers/VLMProviderNew'))
const VLMProviderDetail = lazy(() => import('@/pages/vlm-providers/VLMProviderDetail'))
const VLMAnalyticsPage = lazy(() => import('@/pages/vlm-providers/VLMAnalyticsPage'))
const MarketingOverviewPage = lazy(() => import('@/pages/marketing/MarketingOverviewPage'))
const MarketingCampaignDetailPage = lazy(() => import('@/pages/marketing/CampaignDetailPage'))
const MarketingReviewQueuePage = lazy(() => import('@/pages/marketing/ReviewQueuePage'))
const MarketingTemplatesPage = lazy(() => import('@/pages/marketing/TemplatesPage'))
const MarketingTemplateEditorPage = lazy(() => import('@/pages/marketing/TemplateEditorPage'))
const NotFound = lazy(() => import('@/pages/NotFound'))

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const [hydrated, setHydrated] = useState(useAuthStore.persist.hasHydrated())
  const { isAuthenticated } = useAuthStore()

  useEffect(() => {
    const unsub = useAuthStore.persist.onFinishHydration(() => setHydrated(true))
    setHydrated(useAuthStore.persist.hasHydrated())
    return unsub
  }, [])

  if (!hydrated) return <LoadingFallback />
  if (!isAuthenticated) return <Navigate to="/login" replace />
  return <>{children}</>
}

function LoadingFallback() {
  return (
    <div className="flex h-screen items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary/30 border-t-primary" />
    </div>
  )
}

/**
 * Wraps a route element in a permission check.
 *
 * The permission strings below are the SAME ones `Sidebar.tsx` gates its nav
 * items on, so the menu and the router can never disagree about who may see
 * what. Routes with no nav-level permission (dashboard, settings, the 404) stay
 * ungated on purpose — an admin must always have somewhere to land.
 */
function Gated({ permission, children }: { permission: string; children: React.ReactNode }) {
  return <PermissionRoute permission={permission}>{children}</PermissionRoute>
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ErrorBoundary>
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            {/* Public auth routes — wrapped in AuthLayout */}
            <Route element={<AuthLayout />}>
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              {/* Canonical path. */}
              <Route path="/reset-password" element={<ResetPassword />} />
              {/* Legacy alias: the API sent /auth/reset-password for a route
                  that never existed, so every admin reset email 404'd. Those
                  links are still live for their TTL. */}
              <Route path="/auth/reset-password" element={<ResetPassword />} />
            </Route>

            {/* Protected routes */}
            <Route
              element={
                <ProtectedRoute>
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              {/* Always reachable — the safe landing spots. */}
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/settings" element={<SettingsPage />} />

              <Route path="/tenants" element={<Gated permission="tenants:read"><TenantsPage /></Gated>} />
              <Route path="/tenants/:id" element={<Gated permission="tenants:read"><TenantDetail /></Gated>} />
              <Route path="/users" element={<Gated permission="users:read"><UsersPage /></Gated>} />
              <Route path="/users/:id" element={<Gated permission="users:read"><UserDetail /></Gated>} />
              <Route path="/accounts" element={<Gated permission="accounts:read"><AccountsPage /></Gated>} />
              <Route path="/accounts/:id" element={<Gated permission="accounts:read"><AccountDetailPage /></Gated>} />
              <Route path="/analytics" element={<Gated permission="analytics:read"><AnalyticsPage /></Gated>} />
              <Route path="/audit" element={<Gated permission="audit:read"><AuditPage /></Gated>} />
              <Route path="/audit/:id" element={<Gated permission="audit:read"><AuditDetail /></Gated>} />
              <Route path="/support" element={<Gated permission="support:read"><TicketsPage /></Gated>} />
              <Route path="/support/chat" element={<Gated permission="support:chat"><LiveChatPage /></Gated>} />
              <Route path="/support/:id" element={<Gated permission="support:read"><TicketDetail /></Gated>} />
              <Route path="/jobs" element={<Gated permission="jobs:read"><JobsPage /></Gated>} />
              <Route path="/jobs/:id" element={<Gated permission="jobs:read"><JobDetail /></Gated>} />
              <Route path="/notifications" element={<Gated permission="notifications:read"><NotificationsPage /></Gated>} />
              <Route path="/notifications/new" element={<Gated permission="notifications:read"><NotificationNew /></Gated>} />
              <Route path="/notifications/:id" element={<Gated permission="notifications:read"><NotificationDetail /></Gated>} />
              <Route path="/webhooks" element={<Gated permission="webhooks:read"><WebhooksPage /></Gated>} />
              <Route path="/webhooks/:id" element={<Gated permission="webhooks:read"><WebhookDetail /></Gated>} />
              <Route path="/oauth" element={<Gated permission="oauth:read"><OAuthOverviewPage /></Gated>} />
              <Route path="/oauth/:id" element={<Gated permission="oauth:read"><OAuthClientDetailPage /></Gated>} />
              <Route path="/marketing" element={<Gated permission="marketing:read"><MarketingOverviewPage /></Gated>} />
              <Route path="/marketing/review" element={<Gated permission="marketing:read"><MarketingReviewQueuePage /></Gated>} />
              <Route path="/marketing/templates" element={<Gated permission="marketing:read"><MarketingTemplatesPage /></Gated>} />
              <Route path="/marketing/templates/new" element={<Gated permission="marketing:read"><MarketingTemplateEditorPage /></Gated>} />
              <Route path="/marketing/templates/:id" element={<Gated permission="marketing:read"><MarketingTemplateEditorPage /></Gated>} />
              <Route path="/marketing/campaigns/:id" element={<Gated permission="marketing:read"><MarketingCampaignDetailPage /></Gated>} />
              <Route path="/billing-audit" element={<Gated permission="billing:read"><BillingAuditPage /></Gated>} />
              <Route path="/billing-plans" element={<Gated permission="billing:read"><PlansPage /></Gated>} />
              <Route path="/coupons" element={<Gated permission="credits:read"><CouponsPage /></Gated>} />
              <Route path="/status" element={<Gated permission="system:read"><StatusPage /></Gated>} />
              <Route path="/waitlist" element={<Gated permission="waitlist:read"><WaitlistPage /></Gated>} />
              <Route path="/surveys" element={<Gated permission="surveys:read"><SurveysPage /></Gated>} />
              <Route path="/surveys/:id" element={<Gated permission="surveys:read"><SurveyDetailPage /></Gated>} />
              <Route path="/blacklist" element={<Gated permission="blacklist:read"><BlacklistPage /></Gated>} />
              <Route path="/dpa" element={<Gated permission="dpa:read"><DPAPage /></Gated>} />
              <Route path="/dpa/:id" element={<Gated permission="dpa:read"><DPADetail /></Gated>} />
              <Route path="/review-queue" element={<Gated permission="review:read"><ReviewQueuePage /></Gated>} />
              <Route path="/review-queue/:id" element={<Gated permission="review:read"><ReviewDetail /></Gated>} />
              <Route path="/roles" element={<Gated permission="roles:manage"><RolesPage /></Gated>} />
              <Route path="/roles/:id" element={<Gated permission="roles:manage"><RoleEditPage /></Gated>} />
              <Route path="/content-categories" element={<Gated permission="content:read"><ContentCategoriesPage /></Gated>} />
              <Route path="/api-versions" element={<Gated permission="api_versions:read"><ApiVersionsPage /></Gated>} />
              <Route path="/runtime-settings" element={<Gated permission="settings:read"><RuntimeSettingsPage /></Gated>} />
              <Route path="/countries/document-types" element={<Gated permission="countries:read"><CountryDocumentTypesPage /></Gated>} />
              <Route path="/verifications" element={<Gated permission="tenants:read"><VerificationsPage /></Gated>} />
              <Route path="/verifications/:id" element={<Gated permission="review:read"><VerificationDetail /></Gated>} />
              <Route path="/webhook-deliveries" element={<Gated permission="webhooks:read"><WebhookDeliveriesPage /></Gated>} />
              <Route path="/content-categories/new" element={<Gated permission="content:read"><ContentCategoryNew /></Gated>} />
              <Route path="/content-categories/:id" element={<Gated permission="content:read"><ContentCategoryDetail /></Gated>} />
              <Route path="/vlm-providers" element={<Gated permission="vlm:read"><VLMProvidersPage /></Gated>} />
              <Route path="/vlm-providers/new" element={<Gated permission="vlm:read"><VLMProviderNew /></Gated>} />
              <Route path="/vlm-providers/:id" element={<Gated permission="vlm:read"><VLMProviderDetail /></Gated>} />
              <Route path="/vlm-analytics" element={<Gated permission="vlm:read"><VLMAnalyticsPage /></Gated>} />
              <Route path="/feedback" element={<Gated permission="feedback:read"><FeedbackPage /></Gated>} />
            </Route>

            {/* Defaults */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
        </ErrorBoundary>
        <Toaster />
      </BrowserRouter>
    </QueryClientProvider>
  )
}
